import type { SaleLineResource, SaleTaxResource } from '@repo/api-client';
import { TableCell, TableHead } from '@repo/ui/components/table';
import { type ReactNode, useId } from 'react';

import { formatPaise } from '@/lib/money';

import { formatTaxRate } from '../catalogue/tax';

/**
 * The tax a sale carries (blossom-rest-api#524), in whichever of its three
 * states it is in. Shared by the sale's record and its reprinted duplicate,
 * which carry the same block.
 *
 * - **`TAXED`** — a tax invoice. Every figure is printed **as the API sent
 *   it**. CGST and SGST are each half the tax rounded half up, so together they
 *   can be a paisa more than the tax; the park's own invoice prints exactly
 *   that, and re-deriving the halves here to make them add up would print an
 *   invoice the park never issued.
 * - **`UNTAXED`** — no line was taxed. Nothing is drawn.
 * - **`NOT_RECORDED`** — recorded before tax invoices existed, or imported
 *   from a billing machine. **The tax is unknown, not zero**, and it is said in
 *   words: its figures are null, and a `₹0.00` here would tell whoever files
 *   the park's GST return that no tax was collected on a sale that may well
 *   have carried it. `Sale.test.tsx` fails if one appears.
 */
export function SaleTaxSection({ tax }: { tax: SaleTaxResource }) {
  const headingId = useId();

  if (tax.status === 'UNTAXED') return null;

  if (tax.status === 'NOT_RECORDED') {
    return (
      <section aria-labelledby={headingId} className="space-y-2">
        <Heading id={headingId}>Tax</Heading>
        <p className="text-sm text-foreground">
          Not recorded. This sale was recorded before tax invoices existed, or imported from a
          billing machine, so its tax is unknown, not zero.
        </p>
      </section>
    );
  }

  // Set as an invoice's totals block, under the amounts it sums rather than
  // spread across the field — the figures still end on the amounts' edge.
  return (
    <section aria-labelledby={headingId} className="space-y-3 sm:ml-auto sm:max-w-sm">
      <Heading id={headingId}>Tax invoice</Heading>
      <dl className="space-y-1.5 text-sm">
        <Figure label="Invoice number">{tax.invoiceNumber}</Figure>
        <Figure label="Taxable value">{amount(tax.taxablePaise)}</Figure>
        <Figure label="Tax">{amount(tax.taxPaise)}</Figure>
        <Figure label="CGST">{amount(tax.cgstPaise)}</Figure>
        <Figure label="SGST">{amount(tax.sgstPaise)}</Figure>
        <Figure label="Round off">{amount(tax.roundOffPaise)}</Figure>
      </dl>
    </section>
  );
}

/** The per-line tax columns' headings, drawn only on a `TAXED` sale. */
export function LineTaxHeads() {
  return (
    <>
      <TableHead className="w-24">HSN</TableHead>
      <TableHead className="w-20 text-right">GST</TableHead>
      <TableHead className="w-36 text-right">Taxable value</TableHead>
      <TableHead className="w-32 text-right">Tax</TableHead>
    </>
  );
}

/**
 * One line's HSN, rate, taxable value and tax, in `LineTaxHeads`' order. A line left untaxed in a taxed basket — a mixed basket is one invoice
 * — says so once rather than printing four blanks.
 */
export function LineTaxCells({ line }: { line: SaleLineResource }) {
  const cell = 'text-right font-mono text-foreground numeric';

  return (
    <>
      <TableCell className="font-mono text-foreground numeric">
        {line.hsnCode ?? <span className="font-sans text-muted-foreground">Not taxed</span>}
      </TableCell>
      <TableCell className={cell}>
        {line.taxRateBasisPoints === null ? '' : formatTaxRate(line.taxRateBasisPoints)}
      </TableCell>
      <TableCell className={cell}>
        {line.taxablePaise === null ? '' : formatPaise(line.taxablePaise)}
      </TableCell>
      <TableCell className={cell}>
        {line.taxPaise === null ? '' : formatPaise(line.taxPaise)}
      </TableCell>
    </>
  );
}

/**
 * A figure a `TAXED` block always carries. Null only if the API broke its own
 * contract, and even then it is a dash — never a zero.
 */
const amount = (paise: number | null): string => (paise === null ? '—' : formatPaise(paise));

function Heading({ id, children }: { id: string; children: ReactNode }) {
  return (
    <h2
      id={id}
      className="font-display text-xs font-semibold tracking-[0.16em] text-muted-foreground uppercase"
    >
      {children}
    </h2>
  );
}

/** Label left, figure right, so the figures read down one edge. */
function Figure({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="font-mono text-foreground numeric">{children}</dd>
    </div>
  );
}
